'use client';

import { useState, use, Suspense, FormEvent } from 'react';
import { motion, AnimatePresence, type Variants } from 'framer-motion';
import { Leaf } from 'lucide-react';
import type { IconType } from 'react-icons';

import { 
  FaPhoneAlt, FaEnvelope, FaWhatsapp, FaTelegramPlane, 
  FaTwitter, FaInstagram, FaDiscord, FaLinkedinIn 
} from "react-icons/fa";

import { createClient } from '@/lib/supabase/client';
import { useRouter } from 'next/navigation';
import { useToast } from '@/components/ui/Toast';
import { Skeleton, VolunteerFormSkeleton } from '@/components/ui/Skeleton';
import { BackButton } from '@/components/ui/BackButton';
import { useLocale } from '@/i18n/context';

interface ContactPlatform {
  id: string;
  icon: IconType;
  name: string;
}

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  show: { 
    opacity: 1, 
    transition: { staggerChildren: 0.1 } 
  }
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 20 },
  show: { 
    opacity: 1, 
    y: 0, 
    transition: { type: 'spring' as const, stiffness: 300, damping: 24 } 
  }
};

const inputVariants: Variants = {
  hidden: { opacity: 0, x: -20 },
  show: { opacity: 1, x: 0, transition: { type: 'spring' as const, stiffness: 300, damping: 25 } },
  exit: { opacity: 0, x: 20, transition: { duration: 0.15 } }
};

const CONTACT_PLATFORMS: ContactPlatform[] = [
  { id: 'phone', icon: FaPhoneAlt, name: 'Phone' },
  { id: 'email', icon: FaEnvelope, name: 'Email' },
  { id: 'whatsapp', icon: FaWhatsapp, name: 'WhatsApp' },
  { id: 'telegram', icon: FaTelegramPlane, name: 'Telegram' },
  { id: 'x', icon: FaTwitter, name: 'X' },
  { id: 'instagram', icon: FaInstagram, name: 'Instagram' },
  { id: 'discord', icon: FaDiscord, name: 'Discord' },
  { id: 'linkedin', icon: FaLinkedinIn, name: 'LinkedIn' },
];

interface FormContentProps {
  initialName?: string;
  initialCity?: string;
}

function FormContent({ initialName = '', initialCity = '' }: FormContentProps) {
  const { t, isRtl } = useLocale();
  const [name, setName] = useState<string>(initialName);
  const [country, setCountry] = useState<string>("");
  const [city, setCity] = useState<string>(initialCity);
  const [contactMethod, setContactMethod] = useState<ContactPlatform | null>(null);
  const [contactId, setContactId] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { toast } = useToast();
  const supabase = createClient();
  const router = useRouter();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name || !country || !city || !contactMethod || !contactId) {
      toast.warning(t.volunteer.incompleteFormTitle, t.volunteer.incompleteFormDesc);
      return;
    }
    
    setIsSubmitting(true);
    
    const { error } = await supabase.from('volunteers').insert({
      name,
      country,
      city,
      platform: contactMethod.id,
      contact_id: contactId
    });

    setIsSubmitting(false);

    if (error) {
      toast.error(t.common.error, `${error.message}`);
    } else {
      toast.success(t.volunteer.successTitle, t.volunteer.successMsg, 6000);
      router.refresh();
      router.push('/');
    }
  };

  return (
    <div className="w-full max-w-3xl mx-auto z-10">
      <div className="flex justify-start mb-4">
        <BackButton href="/" label={t.common.back} />
      </div>
      <motion.div 
        initial={{ opacity: 0, scale: 0.96, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="w-full bg-[#F5F0E8]/95 dark:bg-zinc-900/95 backdrop-blur-xl border border-[#206A4F]/20 dark:border-zinc-800 rounded-3xl sm:rounded-[2.5rem] p-6 sm:p-10 md:p-14 shadow-2xl relative transition-colors duration-300"
      >
        <motion.div variants={containerVariants} initial="hidden" animate="show" className="text-center mb-8 md:mb-10">
          <motion.div variants={itemVariants} className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-[#206A4F]/10 dark:bg-emerald-500/20 text-[#206A4F] dark:text-emerald-400 mb-4 border border-[#206A4F]/15 dark:border-emerald-500/30">
            <Leaf size={28} className="sm:w-8 sm:h-8" />
          </motion.div>
          <motion.h1 variants={itemVariants} className="text-2xl sm:text-3xl md:text-4xl font-black text-[#206A4F] dark:text-white mb-2 sm:mb-3">
            {t.volunteer.title}
          </motion.h1>
          <motion.p variants={itemVariants} className="text-sm sm:text-base text-[#206A4F]/70 dark:text-zinc-400 font-medium">
            {t.volunteer.subtitle}
          </motion.p>
        </motion.div>

        <form onSubmit={handleSubmit}>
          <motion.div variants={containerVariants} initial="hidden" animate="show" className="space-y-6">
            
            {/* Full Name */}
            <motion.div variants={itemVariants}>
              <label className="block text-sm font-black text-[#206A4F]/80 dark:text-zinc-300 mb-2 ms-1">
                {t.volunteer.nameLabel}
              </label>
              <input 
                type="text" 
                value={name}
                placeholder={t.contactUs.namePlaceholder}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-5 py-3.5 rounded-2xl bg-white/70 dark:bg-zinc-800/80 border border-[#206A4F]/20 dark:border-zinc-700 text-[#206A4F] dark:text-white font-black focus:outline-none focus:ring-2 focus:ring-[#206A4F] dark:focus:ring-emerald-500 transition-all placeholder:text-zinc-400 shadow-xs" 
              />
            </motion.div>

            {/* Country & City Inputs */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <motion.div variants={itemVariants}>
                <label className="block text-sm font-black text-[#206A4F]/80 dark:text-zinc-300 mb-2 ms-1">
                  {t.volunteer.countryLabel}
                </label>
                <input 
                  type="text" 
                  value={country}
                  placeholder={t.volunteer.selectCountry}
                  onChange={(e) => setCountry(e.target.value)}
                  className="w-full px-5 py-3.5 rounded-2xl bg-white/70 dark:bg-zinc-800/80 border border-[#206A4F]/20 dark:border-zinc-700 text-[#206A4F] dark:text-white font-black focus:outline-none focus:ring-2 focus:ring-[#206A4F] dark:focus:ring-emerald-500 transition-all placeholder:text-zinc-400 shadow-xs" 
                />
              </motion.div>
              
              <motion.div variants={itemVariants}>
                <label className="block text-sm font-black text-[#206A4F]/80 dark:text-zinc-300 mb-2 ms-1">
                  {t.volunteer.cityLabel}
                </label>
                <input 
                  type="text" 
                  value={city}
                  placeholder={t.volunteer.selectCity}
                  onChange={(e) => setCity(e.target.value)}
                  className="w-full px-5 py-3.5 rounded-2xl bg-white/70 dark:bg-zinc-800/80 border border-[#206A4F]/20 dark:border-zinc-700 text-[#206A4F] dark:text-white font-black focus:outline-none focus:ring-2 focus:ring-[#206A4F] dark:focus:ring-emerald-500 transition-all placeholder:text-zinc-400 shadow-xs" 
                />
              </motion.div>
            </div>

            {/* Preferred Contact Method */}
            <motion.div variants={itemVariants} className="pt-6">
              <label className="block text-sm font-black text-[#206A4F]/80 dark:text-zinc-300 mb-3 ms-1">
                {t.volunteer.platformLabel}
              </label>
              <div className="flex flex-col md:flex-row items-stretch md:items-center gap-4 bg-white/50 dark:bg-zinc-800/50 p-4 rounded-3xl border border-[#206A4F]/20 dark:border-zinc-700 shadow-xs">
                
                {/* Dynamic Input Field */}
                <div className="flex-1 overflow-hidden">
                  <AnimatePresence mode="wait">
                    {contactMethod ? (
                      <motion.div 
                        key={contactMethod.id}
                        variants={inputVariants}
                        initial="hidden"
                        animate="show"
                        exit="exit"
                      >
                        <input 
                          type="text" 
                          value={contactId}
                          onChange={(e) => setContactId(e.target.value)}
                          placeholder={`${t.volunteer.contactIdPlaceholder} (${contactMethod.name})...`} 
                          className="w-full px-5 py-3.5 rounded-2xl bg-white/90 dark:bg-zinc-900 border border-[#206A4F]/30 dark:border-zinc-700 text-[#206A4F] dark:text-white font-bold focus:outline-none focus:ring-2 focus:ring-[#206A4F] dark:focus:ring-emerald-500 placeholder:text-zinc-400"
                          autoFocus
                        />
                      </motion.div>
                    ) : (
                      <motion.div 
                        key="placeholder"
                        initial={{ opacity: 0 }} 
                        animate={{ opacity: 1 }} 
                        exit={{ opacity: 0 }}
                        className="h-full min-h-[54px] px-4 py-3 rounded-2xl bg-[#206A4F]/5 dark:bg-zinc-900 text-[#206A4F]/60 dark:text-zinc-400 border border-dashed border-[#206A4F]/30 dark:border-zinc-700 flex items-center justify-center text-xs sm:text-sm font-bold text-center"
                      >
                        {t.volunteer.platformSelectGuide}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Platform Selection Icons */}
                <div className="flex flex-wrap gap-2 justify-center md:justify-end md:w-[60%] shrink-0">
                  {CONTACT_PLATFORMS.map((platform) => {
                    const Icon = platform.icon;
                    const isSelected = contactMethod?.id === platform.id;
                    return (
                      <motion.button 
                        key={platform.id} 
                        type="button" 
                        whileHover={{ scale: 1.1, y: -2 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => setContactMethod(platform)}
                        title={platform.name}
                        className={`p-3 rounded-2xl transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0 flex items-center justify-center cursor-pointer border ${
                          isSelected 
                          ? 'bg-[#206A4F] dark:bg-emerald-500 text-[#F5F0E8] dark:text-black border-transparent shadow-lg font-black scale-105' 
                          : 'bg-white/70 dark:bg-zinc-800 text-[#206A4F] dark:text-zinc-300 border-[#206A4F]/15 dark:border-zinc-700 hover:bg-[#206A4F]/15 dark:hover:bg-zinc-700 hover:shadow-md'
                        }`}
                      >
                        <Icon size={20} />
                      </motion.button>
                    );
                  })}
                </div>

              </div>
            </motion.div>

            {/* Submit Button */}
            <motion.button 
              variants={itemVariants}
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={isSubmitting}
              className="w-full mt-8 bg-[#206A4F] dark:bg-emerald-500 hover:bg-[#18533E] dark:hover:bg-emerald-400 text-[#F5F0E8] dark:text-black py-4 rounded-2xl font-black text-lg shadow-xl hover:shadow-2xl hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.99] transition-all duration-200 relative overflow-hidden group disabled:opacity-70 flex items-center justify-center gap-2 cursor-pointer"
            >
              <span className="relative z-10 flex items-center gap-2">
                {isSubmitting ? <Skeleton className="w-6 h-6 rounded-full bg-white/20" /> : null}
                {isSubmitting ? t.common.loading : t.volunteer.submitBtn}
              </span>
              <div className="absolute inset-0 bg-white/10 dark:bg-black/10 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out" />
            </motion.button>
            
          </motion.div>
        </form>
      </motion.div>
    </div>
  );
}

interface PageProps {
  searchParams: Promise<{ name?: string; city?: string }>;
}

export default function VolunteerFormPage({ searchParams }: PageProps) {
  return (
    <div className="min-h-screen bg-[#F5F0E8] dark:bg-zinc-950 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden transition-colors duration-300">
      <div className="absolute top-0 right-0 w-[40rem] h-[40rem] bg-[#206A4F]/5 dark:bg-emerald-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[30rem] h-[30rem] bg-[#4ade80]/5 dark:bg-emerald-600/10 rounded-full blur-3xl translate-y-1/3 -translate-x-1/3 pointer-events-none" />
      
      <Suspense fallback={<VolunteerFormSkeleton />}>
        <FormContentWrapper searchParams={searchParams} />
      </Suspense>
    </div>
  );
}

function FormContentWrapper({ searchParams }: { searchParams: Promise<{ name?: string; city?: string }> }) {
  const resolvedParams = use(searchParams);
  return (
    <FormContent 
      initialName={resolvedParams.name ?? ''} 
      initialCity={resolvedParams.city ?? ''} 
    />
  );
}